import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";

// Component Imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input } from "../../../revvComponents";

// Action Imports
import {
  updateDocMetaData,
  deleteDocMetaData,
} from "../../../actions/MetalensActions/metalensActions";

import { getTitleFont } from "../../../constants/metalens";

Element.prototype.scrollIntoViewPromise = function (options) {
  this.scrollIntoView(options);
  const parent = this;
  return {
    then(x) {
      const intersectionObserver = new IntersectionObserver(entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            x();
            intersectionObserver.unobserve(parent);
          }, 100);
        }
      });
      intersectionObserver.observe(parent);
    },
  };
};

const MetalensTag = ({
  title,
  tag,
  updateDocMetaDataAction,
  id,
  deleteDocMetaDataAction,
  type,
  titleSuggestions,
  occurances,
  mentions,
  docID,
  sharingHash,
  isEdited,
}) => {
  const titleRef = useRef(null);
  const tagRef = useRef(null);
  const [newTitle, setNewTitle] = useState("");
  const [newTag, setNewTag] = useState("");
  const [titleActive, setTitleActive] = useState(false);
  const [tagActive, setTagActive] = useState(false);
  const [currentTagPosition, setCurrentTagPosition] = useState(0);
  const [deletingMetatag, setDeletingMetatag] = useState(false);

  const _saveChanges = useCallback(
    (titleParam = title) => {
      const data = {
        id,
        meta_key: titleParam,
        text: tag,
      };

      if (tagActive) {
        data.text = newTag;
      }
      if (titleActive) {
        data.meta_key = titleParam;
      }
      // make action call
      if (newTag !== tag || newTitle !== titleParam) {
        updateDocMetaDataAction(docID, data, sharingHash).catch(err => {
          console.error(err);
        });
      }
      setTagActive(false);
      setTitleActive(false);
    },
    [
      docID,
      id,
      newTag,
      newTitle,
      sharingHash,
      tag,
      tagActive,
      title,
      titleActive,
      updateDocMetaDataAction,
    ],
  );

  const _handleOnClickOutsideInput = useCallback(
    event => {
      if (
        event.target &&
        ((titleRef &&
          titleRef.current &&
          !titleRef.current.contains(event.target)) ||
          (tagRef && tagRef.current && !tagRef.current.contains(event.target)))
      ) {
        _saveChanges(newTitle);
      }
    },
    [_saveChanges, newTitle],
  );

  useEffect(() => {
    if (titleRef) {
      document.addEventListener("click", _handleOnClickOutsideInput);
    }
    return () =>
      document.removeEventListener("click", _handleOnClickOutsideInput);
  }, [_handleOnClickOutsideInput]);

  const _editTitle = () => {
    setNewTitle(title);
    setTitleActive(true);
  };

  const _editTag = () => {
    setNewTag(tag);
    setTagActive(true);
  };

  const _moveToMetatag = (event, callback = () => {}) => {
    if (
      !(
        event &&
        event.target &&
        event.target.dataset &&
        (event.target.dataset.id === "metatag_wrapper" ||
          event.target.dataset.id === "metatag_tag")
      )
    ) {
      return;
    }
    let metaTagID = "";
    if (
      mentions &&
      mentions.length &&
      mentions[currentTagPosition].component_data
    ) {
      metaTagID = `${mentions[currentTagPosition].component_data.blockId}-meta-${mentions[currentTagPosition].start}`;
      if (currentTagPosition >= mentions.length - 1) {
        setCurrentTagPosition(0);
      } else {
        setCurrentTagPosition(currentTagPosition + 1);
      }
    }
    const comp = document.getElementById(metaTagID);
    if (comp) {
      comp
        .scrollIntoViewPromise({
          behavior: "smooth",
          block: "center",
        })
        .then(() => {
          callback();
        });
      comp.className += " metatag-glow-tag";
      setTimeout(() => {
        comp.className = "document-meta-highlight";
      }, 2000);
    } else callback();
  };

  return (
    <div
      onClick={_moveToMetatag}
      onTransitionEnd={() => {
        if (deletingMetatag) {
          deleteDocMetaDataAction(docID, id, sharingHash);
        }
      }}
      tabIndex="0"
      role="button"
      data-id="metatag_wrapper"
      onKeyDown={() => {}}
      className={`metalens-tag-wrapper ${
        deletingMetatag ? "metalens-removed" : ""
      }`}>
      <div data-id="metatag_wrapper" className="metalens-tag-inner-wrapper">
        {titleActive ? (
          <div ref={titleRef} className="metalens-tag-edit-wrapper">
            <Input
              autoFocus
              data-test="metalens-input-change-title"
              onChange={e => {
                setNewTitle(e.target.value);
              }}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  _saveChanges(newTitle);
                }
              }}
              value={newTitle}
              placeholder="Metatag Name"
              id="metatag-name-edit"
              className="metalens-tag-edit-input"
              // onBlur={() => setTimeout(() => _saveChanges(newTitle))}
            />
            {/* Dropdown here */}
            {type !== "text" && titleSuggestions && titleSuggestions.length ? (
              <div className="metalens-tag-edit-dropdown">
                <ul className="metalens-tag-edit-dropdown-list">
                  <div className="metalens-tag-revv-ai">
                    <span aria-label="revv-ai-bolt" role="img">
                      ⚡
                    </span>{" "}
                    Revv AI suggestions
                  </div>
                  {titleSuggestions.map(sug => (
                    <div
                      key={`${sug}title-sug`}
                      tabIndex="0"
                      role="button"
                      onKeyDown={() => {}}
                      onClick={() => {
                        setNewTitle(sug);
                        _saveChanges(sug);
                      }}>
                      <li>{sug}</li>
                    </div>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            onClick={e => {
              e.stopPropagation();
              _moveToMetatag(e, _editTitle);
            }}
            data-id="metatag_wrapper"
            tabIndex="0"
            role="button"
            onKeyDown={() => {}}
            className="metalens-tag-title">
            <FontAwesomeIcon
              icon={["far", getTitleFont(title.toLowerCase())]}
              className="metalens-tag-title-icon"
              // data-belongsto="metalens-dropdown"
            />{" "}
            {title}{" "}
            {type !== "text" && (
              <span
                data-belongsto="metalens-dropdown"
                aria-label="revv-ai-bolt"
                role="img">
                ⚡
              </span>
            )}
          </div>
        )}
        {tagActive ? (
          <div ref={tagRef} className="metalens-tag-edit-wrapper">
            <Input
              autoFocus
              data-test="metalens-input-change-tag"
              onChange={e => setNewTag(e.target.value)}
              value={newTag}
              placeholder="Value"
              id="metatag-value"
              className="metalens-tag-input"
              onBlur={() => _saveChanges(title)}
            />
          </div>
        ) : (
          <React.Fragment>
            <div
              onClick={e => {
                e.stopPropagation();
                _moveToMetatag(e, _editTag);
              }}
              tabIndex="0"
              role="button"
              onKeyDown={() => {}}
              data-id="metatag_tag"
              className="metalens-tag-body">
              <span className="metalens-tag-body-title">{tag}</span>
              {occurances !== 0 && occurances !== 1 && (
                <span className="metalens-tag-occurances"> x{occurances}</span>
              )}
              {isEdited && (
                <span className="metalens-tag-isedited">
                  <FontAwesomeIcon
                    icon={["far", "pen"]}
                    className="metalens-tag-isedited-icon"
                  />
                  Edited
                </span>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
      {/* TODO close icon */}
      <div className="metalens-tag-close">
        <FontAwesomeIcon
          onClick={() => setDeletingMetatag(true)}
          tabIndex="0"
          role="button"
          onKeyDown={() => {}}
          icon={["far", "times-circle"]}
          className=""
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  updateDocMetaDataAction: updateDocMetaData,
  deleteDocMetaDataAction: deleteDocMetaData,
};

export default connect(null, mapDispatchToProps)(MetalensTag);
